import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { actingFor, api } from '../api/client';
import { useT } from '../i18n/i18n';
import { TwoFactorSetup, fetchTwoFactor, type TwoFactorStatus } from './twoFactor';

let asked: Promise<TwoFactorStatus | null> | null = null;
let dismissed = false;

export default function TwoFactorPrompt({ children }: { children: ReactNode }) {
  const t = useT();
  const loc = useLocation();
  const [show, setShow] = useState(false);
  const [required, setRequired] = useState(false);
  const [setup, setSetup] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (dismissed || actingFor()) return;
    if (!asked) asked = fetchTwoFactor().catch(() => null);
    let cancelled = false;
    void asked.then((s) => {
      if (cancelled || dismissed || !s) return;
      setRequired(s.required && !s.enabled);
      setShow(s.prompt);
    });
    return () => { cancelled = true; };
  }, []);

  function close() {
    dismissed = true;
    setShow(false);
  }

  async function remind(choice: 'LATER' | 'NEVER') {
    setBusy(true);
    try {
      await api<void>('/me/two-factor/remind', { method: 'POST', token: true, body: { choice } });
    } catch {
      /* the reminder simply comes back next time */
    } finally {
      setBusy(false);
      close();
    }
  }

  if (required) {
    return (
      <section className="card" aria-labelledby="two-factor-prompt-h" style={{ maxWidth: 760, margin: '0 auto' }}>
        <h2 id="two-factor-prompt-h">{t('Your school asks you to turn on two-step sign-in')}</h2>
        <p>{t('Two-step sign-in asks for a code from an app on your phone as well as your password, so someone who learns your password still cannot see your child. Set it up once, and then you can carry on.')}</p>
        <TwoFactorSetup onEnabled={() => window.location.reload()} />
      </section>
    );
  }

  if (!show || loc.pathname.startsWith('/play/')) return <>{children}</>;

  return (
    <>
      <section className="card" aria-labelledby="two-factor-prompt-h">
        <h2 id="two-factor-prompt-h">{t('Protect your account with a code from your phone')}</h2>
        {setup ? (
          <TwoFactorSetup onEnabled={close} onCancel={() => setSetup(false)} />
        ) : (
          <>
            <p>{t('Two-step sign-in asks for a code from an app on your phone as well as your password, so someone who learns your password still cannot get in. It is optional, and you can turn it on or off at any time in Settings, then Account.')}</p>
            <div className="toolbar">
              <button type="button" className="btn" onClick={() => setSetup(true)} disabled={busy}>{t('Set up now')}</button>
              <button type="button" className="btn secondary" onClick={() => void remind('LATER')} disabled={busy}>{t('Remind me later')}</button>
              <button type="button" className="btn ghost" onClick={() => void remind('NEVER')} disabled={busy}>{t('Do not remind me')}</button>
            </div>
          </>
        )}
      </section>
      {children}
    </>
  );
}

export function resetTwoFactorPrompt() {
  asked = null;
  dismissed = false;
}
